// src/pages/Results.js
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  findMutualMatches,
  findPartialMatches,
  organizeMatchesByCategory,
  organizePartialMatchesByCategory,
} from "../data/activities";
import NavBar from "../components/NavBar";

function Results({ quizState, setQuizState }) {
  const navigate = useNavigate();

  // Calculate mutual matches and partial matches
  const mutualMatchIds = findMutualMatches(quizState.responses);
  const organizedMatches = organizeMatchesByCategory(mutualMatchIds);

  const partialMatches = findPartialMatches(quizState.responses);
  const organizedPartialMatches =
    organizePartialMatchesByCategory(partialMatches);

  const totalPeople = quizState.responses.length;
  const hasMultiplePeople = totalPeople >= 3;
  const hasMutualMatches = mutualMatchIds.length > 0;
  const hasPartialMatches = partialMatches.length > 0;
  const hasAnyMatches = hasMutualMatches || hasPartialMatches;

  const handleStartOver = () => {
    // Reset quiz state
    setQuizState({
      currentPersonIndex: 0,
      totalPeople: 2,
      responses: [],
      isComplete: false,
    });
    navigate("/");
  };

  const handleAddMorePeople = () => {
    // Reset for additional people while keeping existing responses
    setQuizState({
      ...quizState,
      totalPeople: quizState.totalPeople + 1,
      isComplete: false,
    });
    navigate("/quiz");
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar></NavBar>
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-8 py-10">
            {/* 3+ People Warning Banner */}
            {hasMultiplePeople && (
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm text-blue-800 leading-relaxed">
                      <strong>Important reminder:</strong> With three or more
                      people, a match means everyone checked it, but you might
                      be comfortable with something with one person and not
                      another. These results are a conversation starter — anyone
                      can say no at any time, and that's always okay.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Title */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                Your Results
              </h1>
              <p className="text-lg text-gray-600">
                {hasMutualMatches && hasPartialMatches
                  ? `${mutualMatchIds.length} mutual ${mutualMatchIds.length === 1 ? "match" : "matches"} (everyone agreed) · ${partialMatches.length} partial ${partialMatches.length === 1 ? "match" : "matches"}`
                  : hasMutualMatches
                    ? `You matched on ${mutualMatchIds.length} ${mutualMatchIds.length === 1 ? "activity" : "activities"}${totalPeople >= 3 ? ` (all ${totalPeople} people agreed)` : ""}`
                    : hasPartialMatches
                      ? `${partialMatches.length} partial ${partialMatches.length === 1 ? "match" : "matches"} (not everyone agreed)`
                      : "No matches yet"}
              </p>
            </div>

            {/* Print Button */}
            {hasAnyMatches && (
              <div className="flex justify-end mb-8">
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-600 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                    />
                  </svg>
                  Print Results
                </button>
              </div>
            )}

            {/* Results by Category */}
            {hasAnyMatches ? (
              <div className="space-y-10">
                {/* Mutual Matches Section */}
                {hasMutualMatches && (
                  <div>
                    <div className="bg-green-50 border-l-4 border-green-500 p-3 mb-6">
                      <p className="text-sm font-semibold text-green-800">
                        ✓ Activities where everyone agreed
                      </p>
                    </div>

                    {organizedMatches.map((category) => (
                      <div key={category.id} className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                          {category.name}
                        </h3>
                        <div className="space-y-2">
                          {category.activities.map((activity) => (
                            <div
                              key={activity.id}
                              className="flex items-center gap-3 p-3.5 bg-green-50 rounded-lg border border-green-200"
                            >
                              <div className="w-5 h-5 bg-green-500 rounded flex items-center justify-center flex-shrink-0">
                                <svg
                                  className="w-3.5 h-3.5 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={3}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </div>
                              <span className="text-gray-800">
                                {activity.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Partial Matches Section */}
                {hasPartialMatches && (
                  <div>
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 mb-6">
                      <p className="text-sm font-semibold text-yellow-800">
                        ⚠️ Partial matches (not everyone agreed)
                      </p>
                    </div>

                    {organizedPartialMatches.map((category) => (
                      <div key={category.id} className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                          {category.name}
                        </h3>
                        <div className="space-y-2">
                          {category.activities.map((activity) => (
                            <div
                              key={activity.id}
                              className="flex items-start gap-3 p-3.5 bg-yellow-50 rounded-lg border border-yellow-200"
                            >
                              <div className="flex-shrink-0 mt-0.5">
                                <div className="text-sm font-semibold text-yellow-700 bg-yellow-100 rounded px-2 py-1">
                                  {activity.count}/{activity.total}
                                </div>
                              </div>
                              <span className="text-gray-800">
                                {activity.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // No matches - encouraging message
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg
                    className="w-10 h-10 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No mutual matches yet
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  That's okay! The most important thing is that you're talking
                  openly about your interests and boundaries. Keep the
                  conversation going.
                </p>
              </div>
            )}

            {/* Footer Message */}
            <div className="mt-10 p-6 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-700 text-center leading-relaxed">
                {hasMutualMatches
                  ? "These matches are a starting point for conversation. Remember, consent is ongoing — you can always change your mind, and communication is key to a healthy, enjoyable experience for everyone."
                  : hasPartialMatches
                    ? "Partial matches show where some (but not all) people agreed. These can be great conversation starters! Remember, consent is ongoing — you can always change your mind, and communication is key to a healthy, enjoyable experience for everyone."
                    : hasMultiplePeople
                      ? "With more people, fewer activities will match for everyone — and that's perfectly normal! These results are a conversation starter. Remember, consent is ongoing — you can always change your mind, and communication is key to a healthy, enjoyable experience for everyone."
                      : "Remember, consent is ongoing — you can always change your mind, and communication is key to a healthy, enjoyable experience for everyone."}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 pt-6 border-t border-gray-200 flex gap-4">
              <button
                onClick={handleStartOver}
                className="flex-1 bg-green-500 text-white py-3.5 rounded-lg font-semibold hover:bg-green-400 transition-colors shadow-sm"
              >
                Start Over
              </button>
              <button
                onClick={handleAddMorePeople}
                className="flex-1 bg-white text-gray-600 py-3.5 rounded-lg font-semibold border-2 border-gray-600 hover:bg-gray-50 transition-colors"
              >
                Add More People
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Results;
